import {
    Button,
    IconButton,
    InputControl,
    InputLabel,
    Loading,
    Spacer,
    StyleFunction,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import {
    useGetProduct2ViProduct2MeasurementUnitQuery,
    useSetProduct2ViProduct2MeasurementUnitMutation,
    useViProductsQuery,
} from "graphql-sdk";
import produce from "immer";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { View } from "react-native";
import { useHandleMutationError } from "../../../hooks";
import { ViPickerRow } from "./vi-picker-row";

export function ViPicker({
    onClose,
    onSave,
    productId,
}: {
    onClose: () => void;
    onSave: () => void;
    productId: string;
}) {
    const styles = useThemedStyle(styleFunc);
    const [t] = useTranslation();
    const toast = useToast();
    const { handleMutationError } = useHandleMutationError();
    const [setRelation] = useSetProduct2ViProduct2MeasurementUnitMutation();

    const [viProducts, setViProducts] = useState([]);

    const { data, loading } = useViProductsQuery({
        variables: {
            pagination: {
                page: 0,
                pageSize: 99999,
            },
        },
        fetchPolicy: "no-cache",
    });

    const { data: product2ViP2MUData, loading: product2ViP2MULoading } =
        useGetProduct2ViProduct2MeasurementUnitQuery({
            variables: {
                productId: productId,
            },
            fetchPolicy: "no-cache",
        });

    useEffect(() => {
        if (product2ViP2MULoading) {
            return;
        }

        setViProducts(
            product2ViP2MUData.getProduct2ViProduct2MeasurementUnit?.map(
                itr => {
                    return {
                        productId: itr.viProduct.id,
                        measurementUnitId: itr.measurementUnit.id,
                        value: itr.value,
                        isDeleted: itr.deletedAt !== null,
                    };
                }
            )
        );
    }, [product2ViP2MUData, product2ViP2MULoading, productId]);

    const onAddViProduct = useCallback(() => {
        setViProducts(
            produce(viProducts, draft => {
                draft?.push({
                    productId: undefined,
                    measurementUnitId: undefined,
                    value: 0,
                });
            }) || []
        );
    }, [viProducts]);

    const onRemoveViProduct = useCallback(
        index => {
            setViProducts(
                produce(viProducts, draft => {
                    draft?.splice(index, 1);
                }) || []
            );
        },
        [viProducts]
    );

    const onProductChange = useCallback(
        (
            index: number,
            viProductId: string,
            value: number,
            measurementUnitId?: string
        ) => {
            setViProducts(
                produce(viProducts, draft => {
                    draft[index].productId = viProductId;
                    draft[index].measurementUnitId = measurementUnitId;
                    draft[index].value = value;
                })
            );
        },
        [viProducts]
    );

    const saveRelations = useCallback(async () => {
        const variables = viProducts.map(itr => {
            return {
                viProductId: itr.productId,
                measurementUnitId: itr.measurementUnitId,
                value: itr.value,
            };
        });

        await handleMutationError(async () => {
            await setRelation({
                variables: {
                    productId: productId,
                    relations: variables,
                },
            });
        }, t("common.saved", "Saved"));
    }, [handleMutationError, productId, setRelation, t, viProducts]);

    if (!data || loading) {
        return <Loading />;
    }

    return (
        <View>
            <InputControl>
                <InputLabel>
                    {t(
                        "backoffice.product.vi.add_inventory_product",
                        "Add inventory product"
                    )}
                    <IconButton
                        name="plus"
                        size="small"
                        onPress={onAddViProduct}
                        style={styles.addViProduct}
                    />
                </InputLabel>

                <Spacer />

                {viProducts.length > 0
                    ? viProducts.map((itm, index) => (
                          <ViPickerRow
                              key={`vi_product_row:${index}`}
                              data={data}
                              index={index}
                              onRemoveViProduct={onRemoveViProduct}
                              onProductChange={onProductChange}
                              productRelation={itm}
                          />
                      ))
                    : null}
            </InputControl>

            <Button
                onPress={async () => {
                    for (let i = 0; i < viProducts.length; i++) {
                        if (
                            viProducts[i].value === 0 ||
                            viProducts[i].value === ""
                        ) {
                            toast.error(
                                t(
                                    "backoffice.product.vi.value_must_be_set",
                                    "Value must be set"
                                )
                            );
                            return;
                        }
                        if (!viProducts[i].productId) {
                            toast.error(
                                t(
                                    "backoffice.product.vi.product_must_be_set",
                                    "Product must be set"
                                )
                            );
                            return;
                        }
                        if (!viProducts[i].measurementUnitId) {
                            toast.error(
                                t(
                                    "backoffice.product.vi.measurement_unit_must_be_set",
                                    "Measurement unit must be set"
                                )
                            );
                            return;
                        }
                    }

                    await saveRelations();
                    onSave();

                    onClose();
                }}
            >
                {t("common.save", "Save")}
            </Button>
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    addViProduct: {
        color: theme.colors.textDark,
    },
    removableRow: {
        backgroundColor: theme.colors.grey50,
        marginBottom: 10,
        borderTopEndRadius: theme.borderRadiusTiny,
        borderBottomEndRadius: theme.borderRadiusTiny,
        zIndex: 1,
    },
});
